import backendModelConfig from './generated/backendModelConfig.json';

const PROD_WS_URL = 'wss://prodaws.mimansakids.com/ws/phoneme-match';
const DEV_WS_URL = 'wss://devaws.mimansakids.com/ws/phoneme-match';

function getDefaultWsUrl() {
  if (process.env.REACT_APP_PHONEME_WS_URL) return process.env.REACT_APP_PHONEME_WS_URL;

  if (typeof window !== 'undefined' && window.location.hostname === 'devpiko.mimansakids.com') {
    return DEV_WS_URL;
  }

  return PROD_WS_URL;
}

export const DEFAULT_WS_URL = getDefaultWsUrl();

export const TARGET_RATE = 16000;
export const VAD_THRESHOLD = 0.002;
export const SILENCE_TIMEOUT_MS = 300;
export const MAX_VOICE_DURATION_MS = 2500;
// Don't ship trailing silence: the backend VAD now trims each utterance to
// speech and re-pads it, so adding dead air here only bloats the chunk.
export const POST_SPEECH_PADDING_MS = 0;
export const FALLBACK_MODELS = backendModelConfig.available || ['W2V2v1.2'];
export const FALLBACK_DEFAULT_MODEL =
  backendModelConfig.default || FALLBACK_MODELS[0] || 'W2V2v1.2';

export function deriveApiBaseUrl(wsUrl) {
  try {
    const url = new URL(wsUrl);
    url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
    url.pathname = url.pathname.replace(/\/ws\/phoneme-match\/?$/, '');
    url.hash = '';
    url.search = '';
    return url.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
}

export function createUuid() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function buildHardwareDetails(sampleRate) {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    deviceMemory: navigator.deviceMemory ?? null,
    client_sample_rate: sampleRate ?? TARGET_RATE,
    client_channels: 1,
  };
}

export function humanizeGameId(gameId) {
  return gameId
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export function previewTarget(target, index) {
  if (target?.label) return target.label;
  if (target?.word) return target.word;
  if (target?.id) return target.id;
  if (Array.isArray(target?.phonemes)) return target.phonemes.join(' ');
  return `Pattern ${index + 1}`;
}

export function buildGameOptions(gameConfig) {
  return Object.entries(gameConfig?.games || {})
    .filter(([gameId]) => gameId !== 'default')
    .map(([gameId, ruleSet]) => ({
      id: gameId,
      title: humanizeGameId(gameId),
      ruleSet,
    }));
}

export function flatten(arrays) {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0);
  const out = new Float32Array(totalLength);
  let offset = 0;
  for (const arr of arrays) {
    out.set(arr, offset);
    offset += arr.length;
  }
  return out;
}

export function appendSilence(buffer, durationMs, sampleRate = TARGET_RATE) {
  const paddingLength = Math.max(0, Math.round((durationMs / 1000) * sampleRate));
  if (paddingLength === 0) return buffer;

  const out = new Float32Array(buffer.length + paddingLength);
  out.set(buffer);
  return out;
}

export function resampleBuffer(buffer, inputRate, outputRate) {
  if (inputRate === outputRate) return buffer;
  const ratio = inputRate / outputRate;
  const newLength = Math.round(buffer.length / ratio);
  const resampled = new Float32Array(newLength);
  for (let i = 0; i < newLength; i += 1) {
    const idx = i * ratio;
    const before = Math.floor(idx);
    const after = Math.min(Math.ceil(idx), buffer.length - 1);
    const t = idx - before;
    resampled[i] = (1 - t) * buffer[before] + t * buffer[after];
  }
  return resampled;
}

export function float32ToPcm16(input) {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i += 1) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export function formatBool(value) {
  if (value === null || value === undefined) return '--';
  return value ? 'Yes' : 'No';
}

export function formatNumber(value, digits = 2) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  return value.toFixed(digits);
}
